import React, { useEffect, useState, useContext, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { UserContext } from "../context/UserContext";
import "./BookDetails.css";
import StarIcon from "../components/StarIcon";

import Avatar1 from "../components/avatars/avatar1";
import Avatar2 from "../components/avatars/avatar2";
import Avatar3 from "../components/avatars/avatar3";
import Avatar4 from "../components/avatars/avatar4";
import Avatar5 from "../components/avatars/avatar5";
import Avatar6 from "../components/avatars/avatar6";
import Avatar7 from "../components/avatars/avatar7";
import Avatar8 from "../components/avatars/avatar8";
import Avatar9 from "../components/avatars/avatar9";
import AvatarDefault from "../components/avatars/AvatarDefault";

const avatarMap = {
  default: AvatarDefault,
  avatar1: Avatar1,
  avatar2: Avatar2,
  avatar3: Avatar3,
  avatar4: Avatar4,
  avatar5: Avatar5,
  avatar6: Avatar6,
  avatar7: Avatar7,
  avatar8: Avatar8,
  avatar9: Avatar9,
};

const BookDetails = () => {
  const { itemId } = useParams();
  const { user } = useContext(UserContext);
  const navigate = useNavigate();

  const [book, setBook] = useState(null);
  const [error, setError] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [averageRating, setAverageRating] = useState(null);
  const [ratingCount, setRatingCount] = useState(0);
  const [reviewError, setReviewError] = useState(null);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [reviewTitle, setReviewTitle] = useState("");
  const [review, setReview] = useState("");
  const [rating, setRating] = useState(0);

  const fetchReviews = useCallback(async (bookId) => {
    try {
      const res = await axios.get(
        `https://bookmatchup.onrender.com/api/review/book/${encodeURIComponent(
          bookId
        )}`
      );
      const reviewsData = res.data;
      setReviews(reviewsData);
      setAverageRating(calculateAverageRating(reviewsData));
      setRatingCount(reviewsData.length);
    } catch (err) {
      console.error("Error fetching reviews:", err);
      setReviewError("Could not load reviews");
    }
  }, []);

  useEffect(() => {
    const fetchBookAndReviews = async () => {
      try {
        const decodedItemId = decodeURIComponent(itemId);
        const res = await axios.get(
          `https://bookmatchup.onrender.com/api/book/by-item/${encodeURIComponent(
            decodedItemId
          )}`
        );
        let bookData = res.data;
        setBook(bookData);

        if (!bookData.description || !bookData.first_sentence) {
          const enrichRes = await axios.get(
            `https://bookmatchup.onrender.com/api/book/book-details/${encodeURIComponent(
              decodedItemId
            )}`
          );
          bookData = enrichRes.data;
          setBook(bookData);
        }

        if (bookData.itemId) {
          await fetchReviews(bookData.itemId);
        }
      } catch (err) {
        console.error("[BOOK DETAILS ERROR]", err);
        setError("Could not load book details.");
      }
    };

    fetchBookAndReviews();
  }, [itemId, fetchReviews]);

  const calculateAverageRating = (reviews) => {
    if (reviews.length === 0) return 0;
    const total = reviews.reduce((sum, r) => sum + r.rating, 0);
    return Math.round((total / reviews.length) * 10) / 10;
  };

  const handleAddToWishlist = async (book) => {
    if (!user) {
      alert("Please log in to add books to your wishlist.");
      navigate("/login");
      return;
    }
    try {
      const addBookRes = await axios.post(
        "https://bookmatchup.onrender.com/api/book/add-book",
        {
          itemId: book.itemId,
          title: book.title,
          author_name: book.author_name?.join(", "),
          cover_url: book.cover_url,
          first_publish_year: book.first_publish_year,
          first_sentence: book.first_sentence || "",
          isbn: book.isbn || [],
          author_key: book.author_key || [],
          subject: book.subject || [],
          subject_facet: book.subject_facet || [],
        }
      );
      const bookId = addBookRes.data._id;
      await axios.post("https://bookmatchup.onrender.com/api/lists/wishlist", {
        userId: user.id,
        bookId,
        title: book.title,
        author_name: book.author_name.join(", "),
        cover_url: book.cover_url,
      });
      alert("Book added to wishlist!");
    } catch (err) {
      console.error("❌ Failed to add to wishlist:", err);
      alert("This book is already on your Wishlist.");
    }
  };

  const handleAddToCompleted = async (book) => {
    if (!user) {
      alert("Please log in to mark books as completed.");
      navigate("/login");
      return;
    }
    try {
      const addBookRes = await axios.post(
        "https://bookmatchup.onrender.com/api/book/add-book",
        {
          itemId: book.itemId,
          title: book.title,
          author_name: book.author_name?.join(", "),
          cover_url: book.cover_url,
          first_publish_year: book.first_publish_year,
          first_sentence: book.first_sentence || "",
          isbn: book.isbn || [],
          author_key: book.author_key || [],
          subject: book.subject || [],
          subject_facet: book.subject_facet || [],
        }
      );
      const bookId = addBookRes.data._id;
      await axios.post("https://bookmatchup.onrender.com/api/lists/completed", {
        userId: user.id,
        bookId,
        title: book.title,
        author_name: book.author_name.join(", "),
        cover_url: book.cover_url,
      });
      alert("Book marked as completed!");
    } catch (err) {
      console.error("❌ Failed to add to completed:", err);
      alert("This book is already on Completed list.");
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setReviewTitle("");
    setReview("");
    setRating(0);
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    if (!book || !user) return;

    const optimisticReview = {
      _id: "temp-" + Date.now(), // Temporary ID for UI
      username: user.username,
      userId: user.id,
      avatar: user.avatar,
      avatarColor: user.avatarColor,
      rating,
      reviewTitle,
      reviewText: review,
    };

    // 1. Optimistic UI update
    setReviews((prev) => [optimisticReview, ...prev]);
    setRatingCount((prev) => prev + 1);
    setAverageRating((prev) => {
      const total = prev * (ratingCount || 0) + rating;
      return Math.round((total / (ratingCount + 1)) * 10) / 10;
    });

    try {
      // 2. Submit review using Open Library itemId
      await axios.post("https://bookmatchup.onrender.com/api/review", {
        userId: user.id,
        bookId: book.itemId, // ✅ Open Library ID instead of Mongo _id
        title: book.title,
        author_name: book.author_name?.join(", ") || "Unknown",
        rating,
        reviewTitle,
        reviewText: review,
      });

      // 3. Refresh reviews from server after slight delay
      setTimeout(() => fetchReviews(book.itemId), 500); // ✅ Matches what's stored
      handleCloseModal();
    } catch (err) {
      console.error("Error submitting review:", err);
      alert("Failed to submit review.");
      // Roll back optimistic review
      setReviews((prev) => prev.filter((r) => r._id !== optimisticReview._id));
      setRatingCount((prev) => Math.max(prev - 1, 0));
    }
  };

  if (error) return <p>{error}</p>;
  if (!book) return <p>Loading book details...</p>;

  return (
    <div className="book-details-container">
      <div className="book-info">
        <h1>{book.title}</h1>
        <div>
          {averageRating !== null && ratingCount > 0 && (
            <div
              className="score-with-star2"
              style={{ marginTop: "8px", fontSize: "1.1rem" }}
            >
              <strong>{averageRating}</strong>{" "}
              <StarIcon filled={true} size={22} />
            </div>
          )}
        </div>
        {book.cover_url && (
          <img
            src={book.cover_url}
            alt="Book Cover"
            className="book-cover-large"
          />
        )}

        <p className="author-name">
          <strong>By:</strong> {book.author_name?.join(", ") || "Unknown"}
        </p>

        {book.first_sentence && (
          <p className="book-sentence">
            <em>
              <strong>First sentence:</strong> {book.first_sentence}
            </em>
          </p>
        )}

        {book.description && (
          <div className="book-description">
            {(() => {
              const desc =
                typeof book.description === "string"
                  ? book.description
                  : book.description?.value;

              if (!desc) return null;

              const isLong = desc.length > 300;
              const preview = desc.slice(0, 300);

              return (
                <>
                  <div className="book-action-buttons">
                    <p>
                      {showFullDescription || !isLong ? desc : `${preview}...`}
                    </p>
                    {isLong && (
                      <button
                        onClick={() =>
                          setShowFullDescription(!showFullDescription)
                        }
                      >
                        {showFullDescription ? "Hide" : "See full details"}
                      </button>
                    )}
                  </div>
                </>
              );
            })()}
          </div>
        )}

        {book.subject && book.subject.length > 0 && (
          <p>
            <strong>Subjects:</strong> {book.subject.slice(0, 5).join(", ")}
          </p>
        )}

        {book.first_publish_year && (
          <p>
            <strong>First Published:</strong> {book.first_publish_year}
          </p>
        )}

        {user && (
          <div className="book-action-buttons">
            <button onClick={() => handleAddToWishlist(book)}>
              Add to Wishlist
            </button>
            <button onClick={() => handleAddToCompleted(book)}>
              Mark as Completed
            </button>
          </div>
        )}
      </div>

      <div className="reviews-section">
        <h2>Reviews</h2>

        {reviewError && (
          <p style={{ color: "red", fontWeight: "bold", marginBottom: "1rem" }}>
            {reviewError}
          </p>
        )}

        {averageRating !== null && ratingCount > 0 && (
          <div className="rating-summary">
            <p>
              <strong>Average Rating:</strong>{" "}
              <span className="score-with-star">
                <span className="highlight-score">{averageRating}</span>
                <StarIcon filled={true} size={22} />
              </span>
            </p>
            <p>
              Based on {ratingCount} review{ratingCount !== 1 && "s"}
            </p>
            <div className="book-action-buttons">
              {user && (
                <button onClick={() => setIsModalOpen(true)}>
                  Leave a Review
                </button>
              )}
            </div>
          </div>
        )}

        {reviews.length === 0 && user && (
          <div style={{ marginTop: "1rem" }}>
            <p>No reviews yet for this book.</p>
            <button
              className="button-danger"
              onClick={() => setIsModalOpen(true)}
            >
              Be the First to Review
            </button>
          </div>
        )}

        {reviews.length > 0 && (
          <ul className="review-list">
            {reviews.map((review) => {
              const AvatarComponent = avatarMap[review.avatar] || AvatarDefault;
              const avatarColor = review.avatarColor || "#222";

              return (
                <li key={review._id} className="review-item">
                  <div className="review-header">
                    <div
                      className="avatar-wrapper"
                      style={{ borderColor: avatarColor }}
                    >
                      <AvatarComponent color={avatarColor} size={55} />
                    </div>
                    <div className="review-username">
                      {review.username ? (
                        <Link to={`/public-profile/${review.userId}`}>
                          {review.username}
                        </Link>
                      ) : (
                        "Anonymous"
                      )}
                    </div>
                  </div>

                  <div className="review-body">
                    <div className="review-stars">
                      {review.reviewTitle && (
                        <div className="review-title">{review.reviewTitle}</div>
                      )}
                      <div className="review-star-icons">
                        {[...Array(5)].map((_, i) => (
                          <StarIcon
                            key={i}
                            filled={i < review.rating}
                            size={22}
                          />
                        ))}
                      </div>
                    </div>
                    <p>{review.reviewText}</p>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* ✨ Review Modal */}
      {isModalOpen && (
        <div className="modal">
          <div className="modal-content">
            <span className="close" onClick={handleCloseModal}>
              &times;
            </span>
            <h2 className="modal-title">Review: {book.title}</h2>

            <form onSubmit={handleSubmitReview}>
              <div className="rating-container">
                {[...Array(5)].map((_, i) => (
                  <span
                    key={i}
                    className={i + 1 <= rating ? "star-filled" : "star"}
                    onClick={() => setRating(i + 1)}
                  >
                    &#9733;
                  </span>
                ))}
              </div>

              <input
                type="text"
                value={reviewTitle}
                onChange={(e) => setReviewTitle(e.target.value)}
                placeholder="Review Title (optional)"
                maxLength={50}
                className="review-title-input"
              />
              <div className="char-counter">{reviewTitle.length}/50</div>

              <textarea
                className="review-textarea"
                value={review}
                onChange={(e) => setReview(e.target.value)}
                placeholder="Write your review here (optional)..."
                maxLength={500}
              />
              <div className="char-counter">{review.length}/500</div>

              <button
                type="submit"
                className="submit-review-button button-style"
              >
                Submit Review
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookDetails;
